import { Gauge } from 'lucide-react';
import type { PortfolioHolding, PortfolioOverview } from '../App';

type RiskBandsProps = {
  overview: PortfolioOverview;
};

type Band = {
  label: string;
  actual: number;
  target: string;
  min?: number;
  max?: number;
  tone: 'cash' | 'core' | 'offensive';
};

export function RiskBands({ overview }: RiskBandsProps) {
  const cashStaging = percentage(
    overview.cashValue + bucketValue(overview.holdings, ['cash', 'staging', 'treasury']),
    overview.estimatedNav
  );
  const coreIndex = percentage(bucketValue(overview.holdings, ['core', 'index', 'broad market']), overview.estimatedNav);
  const offensive = percentage(bucketValue(overview.holdings, ['offensive', 'tactical', 'satellite', 'growth']), overview.estimatedNav);
  const bands: Band[] = [
    { label: 'Cash/staging', actual: cashStaging, target: 'Staging reserve', tone: 'cash' },
    { label: 'Core/index', actual: coreIndex, target: 'Primary risk anchor', tone: 'core' },
    { label: 'Offensive budget', actual: offensive, target: '5-10% context', min: 5, max: 10, tone: 'offensive' }
  ];

  return (
    <section className="dashboard-panel risk-panel" aria-labelledby="risk-heading">
      <div className="panel-heading">
        <div>
          <h2 id="risk-heading">Risk bands</h2>
          <p>Current weights use quoted values where available, then broker fallback values.</p>
        </div>
        <Gauge size={18} aria-hidden="true" />
      </div>

      <div className="risk-band-list">
        {bands.map((band) => (
          <div className="risk-band" key={band.label}>
            <div className="band-copy">
              <strong>{band.label}</strong>
              <span>{band.target}</span>
            </div>
            <div className="band-track" aria-label={`${band.label} ${band.actual.toFixed(1)} percent`}>
              <span className={`band-fill ${band.tone}`} style={{ width: `${Math.min(100, Math.max(0, band.actual))}%` }} />
              {typeof band.min === 'number' ? <span className="band-marker min" style={{ left: `${band.min}%` }} /> : null}
              {typeof band.max === 'number' ? <span className="band-marker max" style={{ left: `${band.max}%` }} /> : null}
            </div>
            <div className="band-value">
              <strong>{band.actual.toFixed(1)}%</strong>
              {typeof band.min === 'number' && typeof band.max === 'number' ? <span>{band.min}-{band.max}% budget</span> : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function bucketValue(holdings: PortfolioHolding[], bucketNames: string[]): number {
  return holdings.reduce((sum, holding) => {
    const bucket = holding.bucket.toLowerCase();
    const role = holding.role.toLowerCase();
    const isMatch = bucketNames.some((name) => bucket.includes(name) || role.includes(name));
    if (!isMatch) return sum;

    if (typeof holding.latestQuotePrice === 'number') return sum + holding.latestQuotePrice * holding.quantity;
    return sum + (holding.brokerMarketValue ?? 0);
  }, 0);
}

function percentage(value: number, total: number): number {
  if (total <= 0) return 0;
  return (value / total) * 100;
}
